import { Currency } from '@/models/purchase-condition/currency-enum';
import { EcoParts } from '@/models/purchase-condition/eco-parts';
import { FrancoUnit, MatrixFrancoUnit } from '@/models/purchase-condition/franco-unit-enum';
import { Circuit } from '@/models/purchase-condition/circuit-enum';
import { IncoTerm } from '@/models/purchase-condition/inco-term-enum';
import { Nullable } from '../type-generators/nullable';
import { Selection } from './selection/selection';

interface BasePurchaseCondition {
  id: string;
  superSupplierCode: Nullable<number>;
  superSupplierCorpName: Nullable<string>;
  legalSupplierCode: number;
  supplierCode: number;
  supplierCorpName: string;
  ean: Nullable<string>;
  supplierRef: Nullable<string>;
  ref: Nullable<string>;
  designation: string;

  beginDate: string;
  currency: Currency;
  billingUnit: string;

  circuit: Circuit;
  incoTerm: IncoTerm;

  pcbPackaging: number;
  francoAmount: number;
  francoUnit: FrancoUnit;

  grossPrice: number;
  cgvDiscount: number;
  otherCgvDiscount: number;
  cpvDiscount: number;
  netPrice: number;

  ecoParts: Nullable<EcoParts>;

  selection: Nullable<Selection>;

  matrixName: string;

  insertionDate: string;
  insertionLdap?: Nullable<string>;
}

export interface OtherPurchaseCondition extends BasePurchaseCondition {
  circuit: 'DIRECT' | 'CROSSDOCK';

  francoUnit: MatrixFrancoUnit;
}

export interface WarehousePurchaseCondition extends BasePurchaseCondition {
  circuit: 'WAREHOUSE';

  francoUnit: MatrixFrancoUnit;
  multiplePCB: number;

  logisticDiscount?: Nullable<number>;
}

export interface LDDPurchaseCondition extends BasePurchaseCondition {
  circuit: 'LDD';

  francoAmount: 0;
  francoUnit: 'Not Concerned';
  incoTerm: 'Delivery Duty Paid (DDP) - Rendu';
  transportPrice: number;
  netPriceWithoutTransportPrice: number;
}

export type PurchaseCondition = OtherPurchaseCondition | WarehousePurchaseCondition | LDDPurchaseCondition;
export type PurchaseConditionDuplicate = PurchaseCondition & { duplicateKey: string };

export function isOtherPurchaseCondition(
  purchaseCondition: PurchaseCondition
): purchaseCondition is OtherPurchaseCondition {
  return purchaseCondition.circuit === Circuit.DIRECT || purchaseCondition.circuit === Circuit.CROSSDOCK;
}

export function isWarehousePurchaseCondition(
  purchaseCondition: PurchaseCondition
): purchaseCondition is WarehousePurchaseCondition {
  return purchaseCondition.circuit === Circuit.WAREHOUSE;
}

export function isLDDPurchaseCondition(
  purchaseCondition: PurchaseCondition
): purchaseCondition is LDDPurchaseCondition {
  return purchaseCondition.circuit === Circuit.LDD;
}
